import { useState, useEffect, useCallback } from 'react'
import { toast } from 'sonner'
import { MediaFile, MediaFolder, MediaServiceConfig } from '../types'

interface UseMediaCoreOptions {
  service: MediaServiceConfig
  open?: boolean
  multiple?: boolean
}

export const useMediaCore = ({ service, open, multiple = false }: UseMediaCoreOptions) => {
  const [rootFolders, setRootFolders] = useState<MediaFolder[]>([])
  const [currentFolder, setCurrentFolder] = useState<MediaFolder | null>(null)
  const [selectedFiles, setSelectedFiles] = useState<MediaFile[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isNewFolderOpen, setIsNewFolderOpen] = useState(false)
  const [isUploadOpen, setIsUploadOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'file' | 'folder'; id: string; name: string } | null>(null)

  const currentFolderId = currentFolder?._id ?? null

  const loadRoot = useCallback(async () => {
    setIsLoading(true)
    try {
      const folders = await service.getRootFolders()
      setRootFolders(folders)
    } catch {
      toast.error('Failed to load media library')
    } finally {
      setIsLoading(false)
    }
  }, [service])

  // When open is provided (modal mode): load on open, reset on close.
  // When open is undefined (page mode): load on mount.
  useEffect(() => {
    if (open === undefined) {
      // Page mode — always load on mount
      loadRoot()
    } else if (open) {
      loadRoot()
    } else {
      setCurrentFolder(null)
      setSelectedFiles([])
      setSearchTerm('')
      setIsNewFolderOpen(false)
      setIsUploadOpen(false)
      setDeleteTarget(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const refresh = useCallback(async () => {
    if (currentFolderId) {
      setIsLoading(true)
      try {
        const folder = await service.getFolder(currentFolderId)
        setCurrentFolder(folder)
      } catch {
        toast.error('Failed to refresh folder')
      } finally {
        setIsLoading(false)
      }
    } else {
      await loadRoot()
    }
  }, [currentFolderId, service, loadRoot])

  const navigateToFolder = useCallback(async (id: string | null) => {
    if (!id) {
      setCurrentFolder(null)
      setSelectedFiles([])
      return
    }
    setIsLoading(true)
    try {
      const folder = await service.getFolder(id)
      setCurrentFolder(folder)
      setSelectedFiles([])
    } catch {
      toast.error('Failed to open folder')
    } finally {
      setIsLoading(false)
    }
  }, [service])

  const toggleSelect = useCallback((file: MediaFile) => {
    if (multiple) {
      setSelectedFiles((prev) => {
        const exists = prev.find((f) => f._id === file._id)
        if (exists) return prev.filter((f) => f._id !== file._id)
        return [...prev, file]
      })
    } else {
      setSelectedFiles([file])
    }
  }, [multiple])

  const clearSelection = useCallback(() => {
    setSelectedFiles([])
  }, [])

  const handleCreateFolder = useCallback(async (name: string) => {
    if (!service.createFolder) return
    try {
      await service.createFolder(name, currentFolderId)
      toast.success('Folder created')
      setIsNewFolderOpen(false)
      await refresh()
    } catch {
      toast.error('Failed to create folder')
    }
  }, [service, currentFolderId, refresh])

  const handleUpload = useCallback(async (file: File) => {
    if (!service.uploadFile) return
    setIsLoading(true)
    try {
      await service.uploadFile(file, currentFolderId)
      toast.success('File uploaded')
      await refresh()
    } catch {
      toast.error('Upload failed')
    } finally {
      setIsLoading(false)
    }
  }, [service, currentFolderId, refresh])

  const handleDeleteFile = useCallback(async (id: string) => {
    if (!service.deleteFile) return
    try {
      await service.deleteFile(id)
      toast.success('File deleted')
      setSelectedFiles((prev) => prev.filter((f) => f._id !== id))
      await refresh()
    } catch {
      toast.error('Failed to delete file')
    }
  }, [service, refresh])

  const handleDeleteFolder = useCallback(async (id: string) => {
    if (!service.deleteFolder) return
    try {
      await service.deleteFolder(id)
      toast.success('Folder deleted')
      await refresh()
    } catch {
      toast.error('Failed to delete folder')
    }
  }, [service, refresh])

  const displayFolders = currentFolder?.folders ?? rootFolders
  const displayFiles = currentFolder?.files ?? []
  const term = searchTerm.toLowerCase()
  const filteredFolders = displayFolders.filter((f) => f.name.toLowerCase().includes(term))
  const filteredFiles = displayFiles.filter((f) => f.name.toLowerCase().includes(term))

  const formatSize = (bytes: number) => {
    if (!bytes) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
  }

  return {
    // navigation
    currentFolderId,
    currentFolderName: currentFolder?.name ?? 'Media Library',
    navigateToFolder,

    // data
    filteredFolders,
    filteredFiles,
    isLoading,

    // selection
    selectedFiles,
    toggleSelect,
    clearSelection,

    // search
    searchTerm,
    setSearchTerm,

    // dialogs
    isUploadOpen,
    setIsUploadOpen,
    isNewFolderOpen,
    setIsNewFolderOpen,
    deleteTarget,
    setDeleteTarget,

    // actions
    handleUpload,
    handleCreateFolder,
    handleDeleteFile,
    handleDeleteFolder,

    // capabilities
    canUpload: !!service.uploadFile,
    canCreateFolder: !!service.createFolder,
    canDelete: !!(service.deleteFile || service.deleteFolder),

    // utils
    formatSize,
  }
}
